import React, { useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Search, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { validateJobRole, suggestJobRoles } from '@/services/openaiService';
import { useDebounce } from '@/hooks/useDebounce';
import { motion } from 'framer-motion';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { PracticeData } from '@/pages/InterviewPractice';

interface RoleDetailsStepProps {
  onSubmit: (role: string, level: 'entry' | 'mid' | 'senior') => void;
  initialData: {
    role: string;
    level: 'entry' | 'mid' | 'senior';
  };
}

const RoleDetailsStep: React.FC<RoleDetailsStepProps> = ({ onSubmit, initialData }) => {
  const [role, setRole] = useState(initialData.role || '');
  const [level, setLevel] = useState<'entry' | 'mid' | 'senior'>(initialData.level || 'mid');
  const [error] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [hasSelected, setHasSelected] = useState(false);
  
  const debouncedRole = useDebounce(role, 500);
  const inputRef = useRef<HTMLInputElement>(null);

  const formatRoleTitle = (title: string) => {
    // Split by spaces and capitalize each word
    return title
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Fetch suggestions as user types
  useEffect(() => {
    if (debouncedRole.length < 2 || hasSelected) {
      setSuggestions([]);
      setOpen(false);
      return;
    }

    const fetchSuggestions = async () => {
      setIsLoading(true);
      setOpen(true);
      try {
        const results = await suggestJobRoles(debouncedRole);
        setSuggestions(results);
        setOpen(results.length > 0);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
        toast.error('Failed to fetch role suggestions');
        setOpen(false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSuggestions();
  }, [debouncedRole, hasSelected]);

  // Validate role when it changes
  useEffect(() => {
    if (!debouncedRole) {
      setIsValid(null);
      return;
    }

    const validateRole = async () => {
      setIsValidating(true);
      try {
        const valid = await validateJobRole(debouncedRole);
        setIsValid(valid);
      } catch (error) {
        console.error('Error validating role:', error);
      } finally {
        setIsValidating(false);
      }
    };

    validateRole();
  }, [debouncedRole]);

  const handleSubmit = () => {
    if (!role.trim()) {
      toast.error('Please enter a role title');
      return;
    }
    
    // Format the role title before submitting
    const formattedRole = formatRoleTitle(role.trim());
    onSubmit(formattedRole, level);
  };

  const handleSuggestionClick = (suggestion: string) => {
    setRole(suggestion);
    setOpen(false);
    setIsValid(true);
    setHasSelected(true);
    if (inputRef.current) {
      inputRef.current.blur();
    }
  };

  // Reset hasSelected when user starts typing new input
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setRole(newValue);
    setHasSelected(false);
    if (newValue.length < 2) {
      setOpen(false);
    }
  };

  return (
    <div className="bg-white/90 rounded-lg border border-[#9b87f5]/30 shadow-lg w-full max-w-3xl px-8 py-10 backdrop-blur-sm">
      <h1 className="text-3xl font-bold text-center mb-3 text-[#1A1F2C]">Practice Job Interview</h1>
      
      <p className="text-center text-[#1A1F2C]/80 mb-8">
        Enter the job role you want to practice interviewing for and we'll help you prepare.
      </p>
      
      <div className="space-y-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#1A1F2C]/50" size={18} />
          <Popover 
            open={open} 
            onOpenChange={(isOpen) => {
              // Only allow opening if we have suggestions or are loading
              if (isOpen && !isLoading && suggestions.length === 0) {
                return;
              }
              setOpen(isOpen);
            }}
            modal={false}
          >
            <PopoverTrigger asChild>
              <div className="relative w-full" onClick={(e) => e.preventDefault()}>
                <Input
                  ref={inputRef}
                  className="bg-white border-[#9b87f5]/30 text-[#1A1F2C] pl-10 py-6 placeholder:text-[#1A1F2C]/50 focus-visible:ring-[#9b87f5]/50"
                  placeholder="Type the role you're interested in (e.g., Software Developer, Data Scientist)"
                  value={role}
                  onChange={handleInputChange}
                  onFocus={() => {
                    if (role.length >= 2 && suggestions.length > 0) {
                      setOpen(true);
                    }
                  }}
                />
                {isValidating ? (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <Loader2 className="animate-spin text-[#9b87f5]" size={18} />
                  </div>
                ) : (
                  isValid !== null && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      {isValid ? (
                        <CheckCircle className="text-green-500" size={18} />
                      ) : (
                        <XCircle className="text-red-500" size={18} />
                      )}
                    </div>
                  )
                )}
              </div>
            </PopoverTrigger>
            <PopoverContent 
              className="p-0 w-[var(--radix-popover-trigger-width)] max-h-[300px] overflow-y-auto bg-white" 
              align="start"
              sideOffset={8}
            >
              <div className="py-2">
                {isLoading ? (
                  <div className="flex items-center justify-center p-4">
                    <Loader2 className="animate-spin text-[#9b87f5]" size={18} />
                    <span className="ml-2">Loading suggestions...</span>
                  </div>
                ) : suggestions.length === 0 ? (
                  <div className="p-4 text-center text-sm text-[#1A1F2C]/70">
                    No roles found
                  </div>
                ) : (
                  <div className="space-y-1">
                    {suggestions.map((suggestion) => (
                      <div
                        key={suggestion}
                        onClick={() => handleSuggestionClick(suggestion)}
                        className="px-4 py-2 text-[#1A1F2C] hover:bg-[#9b87f5]/10 cursor-pointer transition-colors"
                        role="option"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            handleSuggestionClick(suggestion);
                          }
                        }}
                      >
                        {suggestion}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>
          {error && <p className="text-red-400 text-sm mt-1">{error}</p>}
          {isValid === false && !error && (
            <p className="text-amber-500 text-sm mt-1">This doesn't seem like a standard job role. Please check your spelling or select from suggestions.</p>
          )}
        </div>
        
        <div className="pt-4">
          <RadioGroup
            value={level}
            onValueChange={(val) => setLevel(val as 'entry' | 'mid' | 'senior')}
            className="flex justify-center gap-12"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="entry" id="entry" className="border-[#9b87f5]" />
              <Label htmlFor="entry" className="text-[#1A1F2C] font-normal">Entry Level</Label>
            </div>
            
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="mid" id="mid" className="border-[#9b87f5]" />
              <Label htmlFor="mid" className="text-[#1A1F2C] font-normal">Mid Level</Label>
            </div>
            
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="senior" id="senior" className="border-[#9b87f5]" />
              <Label htmlFor="senior" className="text-[#1A1F2C] font-normal">Senior Level</Label>
            </div>
          </RadioGroup>
        </div>
        
        <div className="pt-6 flex justify-center">
          <Button 
            variant="purple"
            className="px-8 py-6 bg-gradient-to-r from-[#9b87f5] to-[#7662DA] hover:from-[#8A76E5] hover:to-[#6552CA] shadow-md"
            onClick={handleSubmit}
          >
            Continue
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RoleDetailsStep; 